import React, { forwardRef } from 'react';

import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import messages from './messages';

type SearchInputProps = {
	placeholderText: string;
	onSearch: (searchText: string) => void;
	isDisabled?: boolean;
	autoFocus?: boolean;
	debounceTime?: number;
	isCompact?: boolean;
	/**
	 * Visual variant of the search input.
	 *
	 * - `'default'` (default): uses ADS TextField default styling (input background + input border).
	 * - `'overlay'`: lighter neutral border on an overlay surface, mirroring the top nav search
	 *   input. Currently consumed by the Rovo Settings modal where the default input styling
	 *   looks too heavy against the modal's elevated surface.
	 */
	variant?: 'default' | 'overlay';
};

const styles = cssMap({
	searchIconWrapper: {
		paddingBlockStart: token('space.025'),
		paddingInlineStart: token('space.100'),
	},
	overlayWrapper: {
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		// Show a focus ring on the wrapper since the inner TextField uses appearance="none"
		// and therefore doesn't render its own border on focus.
		'&:focus-within': {
			borderColor: token('color.border.focused'),
		},
	},
});

export const SearchInput: React.ForwardRefExoticComponent<
	SearchInputProps & React.RefAttributes<unknown>
> = forwardRef(
	(
		{
			placeholderText,
			onSearch,
			isDisabled,
			autoFocus,
			isCompact,
			debounceTime = 500,
			variant = 'default',
		}: SearchInputProps,
		inputRef,
	) => {
		const { formatMessage } = useIntl();
		const [debouncedChange] = useDebouncedCallback((searchText: string) => {
			if (!isDisabled) {
				onSearch(searchText);
			}
		}, debounceTime);

		const textField = (
			<TextField
				isCompact={isCompact}
				aria-label={placeholderText}
				type="search"
				ref={inputRef}
				placeholder={placeholderText}
				onChange={(e) => debouncedChange(e.currentTarget.value)}
				isDisabled={isDisabled}
				autoFocus={autoFocus}
				appearance={variant === 'overlay' ? 'none' : 'standard'}
				elemBeforeInput={
					<Box xcss={styles.searchIconWrapper}>
						<SearchIcon spacing="spacious" color={token('color.icon')} label="" />
					</Box>
				}
				aria-describedby="search-description"
			/>
		);

		return (
			<>
				{variant === 'overlay' ? <Box xcss={styles.overlayWrapper}>{textField}</Box> : textField}
				<VisuallyHidden>
					<div id="search-description">{formatMessage(messages.screenReaderPrompt)}</div>
				</VisuallyHidden>
			</>
		);
	},
);
